import React from 'react';

/**
 * IntensityIndicator - Visual gauge showing perfume intensity
 * 
 * @param {string} intensity - "Sutil" | "Intenso" | "Monstruosa"
 * @param {string} size - "sm" (for cards) | "md" (for detail page)
 * @param {boolean} showLabels - Show text labels below the bar
 */
const IntensityIndicator = ({ intensity = "Intenso", size = "sm", showLabels = false }) => {
  // Calculate position based on intensity (0-100%)
  const getPosition = () => {
    switch (intensity) {
      case 'Sutil':
        return 16.5; // 1/6 of the bar (left section)
      case 'Intenso':
        return 50; // Center
      case 'Monstruosa':
        return 83.5; // 5/6 of the bar (right section)
      default:
        return 50;
    }
  };

  const position = getPosition();
  
  // Size configurations
  const sizes = {
    sm: {
      barHeight: 'h-1.5',
      triangleSize: 'w-2 h-2',
      containerPadding: 'pt-2.5 pb-1',
      fontSize: 'text-[9px]',
      gap: 'gap-0.5'
    },
    md: {
      barHeight: 'h-2.5',
      triangleSize: 'w-3 h-3',
      containerPadding: 'pt-4 pb-2',
      fontSize: 'text-xs',
      gap: 'gap-1'
    }
  };

  const s = sizes[size] || sizes.sm;

  return (
    <div className={`w-full ${s.containerPadding}`}>
      {/* Container for triangle and bar */}
      <div className="relative">
        {/* Triangle indicator */}
        <div 
          className="absolute -top-2 transform -translate-x-1/2 transition-all duration-300"
          style={{ left: `${position}%` }}
        >
          <div
            className={`${s.triangleSize} bg-brand-black`}
            style={{
              clipPath: 'polygon(50% 100%, 0% 0%, 100% 0%)',
              filter: 'drop-shadow(0 1px 2px rgba(17,17,24,0.35))'
            }}
          />
        </div>

        {/* Gradient bar - Petróleo → Cobalto → Magenta (paleta de marca) */}
        <div
          className={`w-full ${s.barHeight} rounded-full overflow-hidden`}
          style={{
            background: 'linear-gradient(to right, #1A5C6D, #1B4FD8, #C2185B)',
            boxShadow: '0 0 8px rgba(27, 79, 216, 0.25), inset 0 1px 2px rgba(0,0,0,0.15)'
          }}
        />

        {/* Glow effect under the indicator position */}
        <div
          className="absolute top-0 h-full w-4 rounded-full transition-all duration-300"
          style={{
            left: `calc(${position}% - 8px)`,
            background: position < 33
              ? 'radial-gradient(ellipse, rgba(26,92,109,0.5) 0%, transparent 70%)'
              : position < 66
              ? 'radial-gradient(ellipse, rgba(27,79,216,0.5) 0%, transparent 70%)'
              : 'radial-gradient(ellipse, rgba(194,24,91,0.5) 0%, transparent 70%)',
            filter: 'blur(4px)'
          }}
        />
      </div>

      {/* Labels */}
      {showLabels && (
        <div className={`flex justify-between mt-2 ${s.fontSize} text-brand-black/50`}>
          <span className={intensity === 'Sutil' ? 'text-brand-petroleum font-medium' : ''}>Sutil</span>
          <span className={intensity === 'Intenso' ? 'text-brand-cobalt font-medium' : ''}>Intenso</span>
          <span className={intensity === 'Monstruosa' ? 'text-brand-magenta font-medium' : ''}>Monstruosa</span>
        </div>
      )}
    </div>
  );
};

export default IntensityIndicator;
